/**
 * Who is holding this phone.
 *
 * Auth is deliberately light (brief §3): a per-surveyor access token arrives in
 * the assignment SMS as a link, the device keeps it, and `GET /api/me` turns it
 * into an id and a name. There is no password infrastructure and no session.
 *
 * The identity is CACHED, and that is not an optimisation. A surveyor opens the
 * app in a basement; `/api/me` cannot be reached; the device still has to stamp
 * `createdBy` on a survey it creates there (spec §2.1), or the first sync has no
 * ownership claim to check. The cache is the answer to "who am I offline".
 *
 * The token is read out of the URL once and then removed from it, so a link
 * shared over a shoulder or left in a screenshot of the address bar stops being
 * a live credential the moment the app has it.
 */

import { looksLikeAccessToken } from '../../core/tokens';

export interface Identity {
  id: string;
  name: string;
}

const TOKEN_KEY = 'upsurv.token';
const IDENTITY_KEY = 'upsurv.identity';
const DEVICE_KEY = 'upsurv.device';

/* ───────────────────────── URL extraction (pure) ───────────────────────── */

/** The `#` or `#!` a fragment may start with, so it can be put back afterwards. */
function fragmentPrefix(source: string): string {
  return /^#!?/.exec(source)?.[0] ?? '';
}

/**
 * Is this fragment carrying parameters DIRECTLY, rather than a route?
 *
 * `assignmentLink()` in src/core/sms.ts builds `https://…/#t=<token>` — the
 * token sits in the fragment with no `?` and no path in front of it, because a
 * fragment never reaches the origin and so cannot leak the credential into a
 * server log. A route fragment (`#/s/abc`) starts with a slash and is not this.
 */
function fragmentParams(source: string): string | null {
  if (source.indexOf('?') >= 0) return null;
  const body = source.slice(fragmentPrefix(source).length);
  if (!body || body.startsWith('/') || !body.includes('=')) return null;
  return body;
}

function without(query: string, names: readonly string[]): string {
  const params = new URLSearchParams(query);
  for (const name of names) params.delete(name);
  return params.toString();
}

/**
 * Every query-string-shaped run of parameters in a location, in the order they
 * should be searched.
 *
 * Three forms reach the app and all three are read, because all three get sent:
 *   `?x=…`      — pasted onto the origin as a real query.
 *   `#/…?x=…`   — someone forwarding a deep link that still carries it.
 *   `#x=…`      — the bare fragment the SMS and QR links themselves use.
 *
 * Shared with `src/ui/state/onboardingClaim.ts` (D-013), which reads a
 * different parameter out of the same three places. One implementation, because
 * two copies of URL parsing are two things free to disagree about what a link
 * means — and the thing they would disagree about is a credential.
 */
export function paramSources(search: string, hash: string): string[] {
  const queries: string[] = [];
  for (const source of [search, hash]) {
    const queryAt = source.indexOf('?');
    if (queryAt >= 0) queries.push(source.slice(queryAt + 1));
  }
  const bare = fragmentParams(hash);
  if (bare) queries.push(bare);
  return queries;
}

/**
 * First value of any of `names` that `accept` vouches for.
 *
 * The shape check is not optional and not the caller's business to skip: a
 * stored junk credential would fail every request until the device was reset,
 * so anything that is not shaped right is IGNORED rather than kept.
 */
export function extractParam(
  search: string,
  hash: string,
  names: readonly string[],
  accept: (value: string) => boolean,
): string | null {
  for (const query of paramSources(search, hash)) {
    const params = new URLSearchParams(query);
    for (const name of names) {
      const candidate = params.get(name);
      if (candidate !== null && accept(candidate)) return candidate;
    }
  }
  return null;
}

/**
 * The same location string with the named parameters removed, preserving
 * whatever else was in it. Handles a bare query ("?t=x&debug=1"), a fragment
 * carrying one ("#/s/abc?t=x") and the link's bare fragment ("#t=x"), because
 * all three reach the app and none of them may survive into history.
 */
export function stripParams(source: string, names: readonly string[]): string {
  const queryAt = source.indexOf('?');
  if (queryAt >= 0) {
    const head = source.slice(0, queryAt);
    const rest = without(source.slice(queryAt + 1), names);
    return rest ? `${head}?${rest}` : head;
  }

  const bare = fragmentParams(source);
  if (bare === null) return source;
  const rest = without(bare, names);
  return rest ? `${fragmentPrefix(source)}${rest}` : '';
}

/** The parameter names an assignment link may carry its access token on. */
export const TOKEN_PARAMS: readonly string[] = ['t', 'token'];

/** Pull an access token out of a location. */
export function extractToken(search: string, hash: string): string | null {
  return extractParam(search, hash, TOKEN_PARAMS, looksLikeAccessToken);
}

/** The same location with the token parameter removed. */
export function stripToken(source: string): string {
  return stripParams(source, TOKEN_PARAMS);
}

/* ───────────────────────────── local storage ───────────────────────────── */

/**
 * Private browsing and a full disk both make localStorage throw on write, and
 * neither is a reason to take down a survey the surveyor is standing in the
 * middle of. Storage is best-effort; IndexedDB is where the work actually lives.
 */
function safeGet(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* best effort */
  }
}

function safeRemove(key: string): void {
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    /* best effort */
  }
}

export function readToken(): string | null {
  const stored = safeGet(TOKEN_KEY);
  return stored && looksLikeAccessToken(stored) ? stored : null;
}

export function writeToken(token: string): void {
  safeSet(TOKEN_KEY, token);
}

export function clearToken(): void {
  safeRemove(TOKEN_KEY);
  safeRemove(IDENTITY_KEY);
}

export function readCachedIdentity(): Identity | null {
  const raw = safeGet(IDENTITY_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Identity>;
    if (typeof parsed.id === 'string' && typeof parsed.name === 'string') {
      return { id: parsed.id, name: parsed.name };
    }
  } catch {
    /* a corrupt cache is no cache */
  }
  return null;
}

export function writeCachedIdentity(identity: Identity): void {
  safeSet(IDENTITY_KEY, JSON.stringify(identity));
}

/**
 * A stable id for this phone, minted on first run.
 *
 * Sync forensics only — it is stamped on pushes as `x-device-id` and never
 * takes part in the conflict ladder (see sync.ts). If storage is unavailable
 * the id is per-session, which degrades the forensics and nothing else.
 */
export function deviceId(): string {
  const existing = safeGet(DEVICE_KEY);
  if (existing) return existing;
  const minted = `dev-${globalThis.crypto?.randomUUID?.() ?? String(Date.now())}`;
  safeSet(DEVICE_KEY, minted);
  return minted;
}

export interface ClaimOutcome {
  /** The token now in force — freshly claimed, or the one already stored. */
  token: string | null;
  /** True only when this page load carried a token in its URL. */
  claimed: boolean;
}

/**
 * Claim a token from the current URL, if there is one, and clean the URL.
 *
 * The rewrite is `replaceState`, never a push: the entry carrying the token is
 * REPLACED, so pressing Back cannot walk to a URL that still holds a live
 * credential.
 *
 * A freshly claimed token lands on `#/access` rather than on the survey list.
 * The exchange is not free — it has to reach `/api/me`, and it can be refused
 * because the surveyor was deactivated — so it gets a screen that can say so
 * instead of a list that would silently render empty.
 */
export function claimTokenFromLocation(): ClaimOutcome {
  if (typeof window === 'undefined') return { token: null, claimed: false };

  const found = extractToken(window.location.search, window.location.hash);
  if (!found) return { token: readToken(), claimed: false };

  writeToken(found);
  const cleanedHash = stripToken(window.location.hash);
  const cleaned =
    window.location.pathname +
    stripToken(window.location.search) +
    // Whatever the link pointed at, the token has to come off it first; if it
    // pointed at nothing (the plain SMS link) the access screen is the target.
    (cleanedHash && cleanedHash !== '#' ? cleanedHash : '#/access');
  window.history.replaceState(null, '', cleaned || '/#/access');
  return { token: found, claimed: true };
}
